import React, { useEffect, useState } from 'react';
import { compose } from 'redux';
import classNames from 'classnames';
import { string, func, bool } from 'prop-types';
import { AiTwotoneHeart } from 'react-icons/ai';
import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage,useIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import { GrLocation } from 'react-icons/gr';
import { AspectRatioWrapper, ResponsiveImage } from '..';

import axios from 'axios';
import css from './ListingCard.module.css';
import { apiBaseUrl } from '../../util/api';
import { withRouter } from 'react-router-dom';
import { createResourceLocatorString } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

const DESC_MAX_WORDS = 80;
const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);

  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);

  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
        </div>
      ) : null}
    </div>
  );
};

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.currentListing listing entity with image data
 * @param {Function?} props.setActivePropsMaybe mouse enter/leave handlers for map highlighting
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {Object?} props.style the background color for the listing card with no image
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const {
    currentListing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    showListingImage,
    style,
  } = props;

  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  // Render the listing image only if listing images are enabled in the listing type
  return showListingImage ? (
    <AspectRatioWrapper
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <LazyImage
        rootClassName={css.rootForImage}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
      />
    </AspectRatioWrapper>
  ) : (
    <ListingCardThumbnail
      style={style}
      listingTitle={title}
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      setActivePropsMaybe={setActivePropsMaybe}
    />
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const routes = useRouteConfiguration();
  const [isFavourite, setIsFavourite] = useState(null);
  const [favouriteLoading, setFavouriteLoading] = useState('');
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo,
    currentUser,
    history,
  } = props;
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;

  const classes = classNames(rootClassName || css.root, className);
  const { title = '', price, publicData, description = '' } = currentListing.attributes;
  const address = publicData?.locationFromUser?.selectedPlace?.address;
  const trimmedDescription =
    description?.length > DESC_MAX_WORDS
      ? description.slice(0, DESC_MAX_WORDS) + '...'
      : description;

    const slug = createSlug(title);

  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;

  const { listingType, cardStyle } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);
  // const author = ensureUser(listing.author);
  // const authorName = author.attributes.profile.displayName;
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const toggleFavourites = async e => {
    e.stopPropagation();
    setFavouriteLoading(id);
    const userId = currentUser?.id?.uuid;

    /*
    While logged in, currentUser is getting undefined
    and that's why if condition isn't working correctly
    */
    // if (!userId) {
    //   return history.push(
    //     createResourceLocatorString('AuthenticationPage', routes, { tab: 'login' })
    //   );
    // }

    const toggleUrl = `${apiBaseUrl()}/api/favourites/toggle/${id}`;

    return axios
      .get(toggleUrl, { withCredentials: true })
      .then(res => {
        res.data.message === 'Favourite added' ? setIsFavourite(true) : setIsFavourite(false);
        setFavouriteLoading('');
      })
      .catch(err => {
        setFavouriteLoading('');
      });
  };

  useEffect(() => {
    if (isFavourite === null && currentUser?.id?.uuid) {
      currentUser.favourites?.includes(id) ? setIsFavourite(true) : setIsFavourite(false);
    }
  }, [currentUser]);

  return (
    <section
      className={classes}
      onClick={e => {
        history.push(createResourceLocatorString('ListingPage', routes, { id, slug }));
      }}
    >
      <AspectRatioWrapper
        width={aspectWidth}
        height={aspectHeight}
        className={css.aspectRatioWrapper}
        {...setActivePropsMaybe}
      >
        <LazyImage
          alt={title}
          image={firstImage}
          variants={variants}
          sizes={renderSizes}
          rootClassName={css.rootForImage}
        />
        <button
          type="button"
          className={classNames(css.favouritesSpan, {
            [css.favourited]: isFavourite,
            [css.favouriteLoading]: favouriteLoading === id,
          })}
          onClick={toggleFavourites}
        >
          <AiTwotoneHeart />
        </button>
      </AspectRatioWrapper>
      <div className={css.info}>
        {/* <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} /> */}
        <div
          className={css.title}
          onClick={() => {
            history.push(createResourceLocatorString('ListingPage', routes, { id, slug }));
          }}
        >
          {richText(title, {
            longWordClass: css.longWord,
            longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
          })}
        </div>
        <p className={css.desc}>{trimmedDescription}</p>
        <p className={css.locationP}>
          <GrLocation />
          <span>{address}</span>
        </p>
        {/* <button className={css.viewNo}>
          <img src={call} />
          View Number
        </button> */}
        {/* <div className={css.mainInfo}>
          {showAuthorInfo ? (
            <div className={css.authorInfo}>
              <FormattedMessage id="ListingCard.author" values={{ authorName }} />
            </div>
          ) : null}
        </div> */}
      </div>
      <button className={css.getBstPrc}>Get Best Price</button>
    </section>
  );
};


export default compose(withRouter)(ListingCard);
